/**
 * LayerIconPreview Component
 *
 * Renders a preview of a layer's icon with its configured styling.
 * For symbol layers (WFS, GeoJSON), displays the actual FontAwesome icon
 * with color, halo, and shadow effects. Falls back to generic type icons
 * for other layer types.
 */

import React, { useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMap, faMapMarkerAlt, faBroadcastTower, faChartArea, faFile } from '@fortawesome/free-solid-svg-icons';
import { Layer } from '../../../types/layer';
import { faIconToSvg, getDefaultIcon } from '../../layers2/utils/iconUtils';
import { getLayerTypeIcon } from '../utils/layerDrawerUtils';

// Generic icon mapping for non-symbol layers
const iconMap: Record<string, any> = {
  'map': faMap,
  'map-marker-alt': faMapMarkerAlt,
  'broadcast-tower': faBroadcastTower,
  'chart-area': faChartArea,
  'file': faFile
};

interface LayerIconPreviewProps {
  layer: Layer;
  size?: number;
  className?: string;
}

/**
 * Strip the FontAwesome style prefix from a class string and return the
 * bare icon name expected by `faIconToSvg`. Accepts inputs like
 *   "fa-solid fa-smog"  → "smog"
 *   "fa-regular fa-bell" → "bell"
 *   "fa-brands fa-github" → "github"
 *   "smog"              → "smog"   (already-bare)
 * Returns null when the input does not yield a parsable name.
 */
function parseFaIconClass(raw: unknown): string | null {
  if (typeof raw !== 'string' || !raw.trim()) return null;
  const match = raw.match(/fa-(?:solid|regular|brands|light|thin|duotone)\s+fa-([a-z0-9-]+)/i);
  if (match) return match[1];
  const bare = raw.replace(/^fa-/, '').trim();
  return /^[a-z0-9-]+$/i.test(bare) ? bare : null;
}

/**
 * Extracts icon style configuration from layer config
 */
function getIconStyleFromConfig(layer: Layer) {
  const config = layer.config || {};
  const style = config.style || {};

  // Only process if this is a symbol layer
  if (style.type !== 'symbol') {
    return null;
  }

  const paint = style.paint || {};

  return {
    iconName: style.icon || getDefaultIcon(layer.name),
    color: paint['icon-color'] || '#3b82f6',
    size: 1, // Fixed size for preview, we'll scale with CSS
    haloColor: style.haloColor || paint['icon-halo-color'],
    haloWidth: style.haloWidth ?? paint['icon-halo-width'] ?? 0,
    shadowBlur: style.shadowBlur ?? 0,
    shadowColor: style.shadowColor
  };
}

/**
 * For type='live' rows we don't carry a Mapbox `style.symbol` block —
 * the live provider renders DOM markers itself. The drawer card icon
 * comes from `config.icon` (FontAwesome class string) instead. Returns
 * the same shape as `getIconStyleFromConfig` so the rest of the
 * component can treat both paths uniformly.
 */
function getLiveLayerIconStyle(layer: Layer) {
  if (layer.type !== 'live') return null;
  const config = layer.config || {};
  const iconName = parseFaIconClass(config.icon);
  if (!iconName) return null;
  // Live rows may override the chooser icon colour via `config.iconColor`.
  // Default mirrors the existing symbol-layer default so untouched rows
  // (Live Weather, Live Aircraft) keep rendering as they did before.
  const color =
    typeof config.iconColor === 'string' && config.iconColor.trim()
      ? config.iconColor
      : '#3b82f6';
  return {
    iconName,
    color,
    size: 1,
    haloColor: undefined,
    haloWidth: 0,
    shadowBlur: 0,
    shadowColor: undefined,
  };
}

export const LayerIconPreview: React.FC<LayerIconPreviewProps> = ({
  layer,
  size = 20,
  className = ''
}) => {
  // Generate styled SVG for symbol layers OR for type='live' rows whose
  // FontAwesome class is set in `config.icon`.
  const styledSvg = useMemo(() => {
    const iconStyle = getIconStyleFromConfig(layer) ?? getLiveLayerIconStyle(layer);

    if (!iconStyle) {
      return null;
    }

    const svg = faIconToSvg(iconStyle.iconName, {
      color: iconStyle.color,
      size: iconStyle.size,
      haloColor: iconStyle.haloColor,
      haloWidth: iconStyle.haloWidth,
      shadowBlur: iconStyle.shadowBlur,
      shadowColor: iconStyle.shadowColor
    });

    return svg;
  }, [layer]);

  // If we have a styled SVG, render it
  if (styledSvg) {
    // Convert SVG to data URL for inline display
    const dataUrl = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(styledSvg)}`;

    return (
      <div
        className={`layer-icon-preview layer-icon-preview--styled ${className}`}
        style={{ width: size, height: size }}
        title={`${layer.type.toUpperCase()} - Symbol layer`}
      >
        <img
          src={dataUrl}
          alt={`${layer.name} icon`}
          style={{ width: '100%', height: '100%', objectFit: 'contain' }}
        />
      </div>
    );
  }

  // Fall back to generic type icon
  const genericIcon = iconMap[getLayerTypeIcon(layer.type)];

  return (
    <div
      className={`layer-icon-preview layer-icon-preview--generic ${className}`}
      style={{ width: size, height: size }}
      title={layer.type.toUpperCase()}
    >
      <FontAwesomeIcon icon={genericIcon} style={{ fontSize: size * 0.7 }} />
    </div>
  );
};

export default LayerIconPreview;
